import I18n from 'lib/i18n'
import { kea } from 'kea'
import PropTypes from 'prop-types'
import { put } from 'redux-saga/effects'
import naturalCompare from 'string-natural-compare'

import formatPrice from 'lib/utils/format-price'
import { clearFilterState, getNewFilters } from './utils'

import sceneLogic from '~/scenes/search/logic'

export default kea({
  path: () => ['scenes', 'search', 'searchFilters'],

  connect: {
    props: [sceneLogic, ['filter', 'isSmallScreen', 'isFullScreenMap', 'countries', 'results']],
    actions: [sceneLogic, ['updateFilter', 'setOpenFilters', 'clearFilters']]
  },

  actions: () => ({
    openFilter: filterType => ({ filterType }),
    applyFilter: nextFilterType => ({ nextFilterType }),
    updateUnsavedFilter: updates => ({ updates }),
    clearFilter: filterType => ({ filterType }),
    clearChanges: true,
    setSuggestionResults: results => ({ results }),
    clearSuggestionResults: true
  }),

  reducers: ({ actions }) => ({
    showSearchBtnOnClearAll: [
      false,
      {
        [actions.clearFilter]: () => true,
        [actions.openFilter]: () => false
      }
    ],
    suggestionsResults: [
      [],
      {
        [actions.setSuggestionResults]: (_, { results }) => results,
        [actions.clearSuggestionResults]: () => []
      }
    ],
    filterChanges: [
      {},
      {
        [actions.updateUnsavedFilter]: (state, payload) => ({ ...state, ...payload.updates }),
        [actions.clearChanges]: () => ({})
      }
    ]
  }),

  selectors: ({ selectors }) => ({
    unsavedFilter: [
      () => [selectors.filter, selectors.filterChanges, selectors.countries],
      (filter, changes, countries) => ({
        ...filter,
        ...changes,
        countries
      }),
      (filter, changes, countries) => ({ ...filter, ...changes, countries }),
      PropTypes.object
    ],

    hasChangesToClear: [
      () => [selectors.unsavedFilter, selectors.activeSubMenu, selectors.isSmallScreen],
      (filter, activeSubMenu, isSmallScreen) => {
        if (!activeSubMenu) {
          return false
        }
        let clearState = clearFilterState({ filterType: activeSubMenu, isSmallScreen, allFilters: false, filter })
        if (activeSubMenu === 'location') {
          delete clearState.center
          delete clearState.zoom
          delete clearState.blue
          delete clearState.bounds
        }

        return Object.keys(clearState).filter(key => (filter[key] || '') !== (clearState[key] || '')).length > 0
      },
      PropTypes.bool
    ],

    isPriceFilterVisible: [
      () => [selectors.filter, selectors.isSmallScreen],
      (filter, isSmallScreen) => !isSmallScreen,
      PropTypes.bool
    ],

    isLanguageFilterVisible: [
      () => [selectors.filter, selectors.isSmallScreen],
      (filter, isSmallScreen) => !isSmallScreen,
      PropTypes.bool
    ],

    isSubMenuActive: [
      () => [selectors.isSmallScreen, selectors.filter],
      (isSmallScreen, { filters }) => {
        if (isSmallScreen || !filters) {
          return false
        }

        return ['age', 'price', 'location', 'all', 'language', 'category_tags'].some(filterItem =>
          filters.includes(filterItem)
        )
      },
      PropTypes.bool
    ],

    activeSubMenu: [
      () => [selectors.filter],
      ({ filters }) =>
        filters
          ? filters
              .split(',')
              .filter(filter => filter !== 'map')
              .join()
          : '',
      PropTypes.string
    ],

    isMapFilterActive: [
      () => [selectors.filter],
      ({ location, default_location: defaultLocation }) => !!location && defaultLocation !== location,
      PropTypes.bool
    ],

    isLocationFilterActive: [
      () => [selectors.filter, selectors.isMapFilterActive],
      ({ location_types: locationTypes }, isMapFilterActive) => isMapFilterActive || !!locationTypes,
      PropTypes.bool
    ],

    activeFiltersCount: [
      () => [selectors.filter, selectors.isPriceFilterVisible],
      (filter, isPriceFilterVisible) => {
        const countableFilters = ['search', 'flags']
        let result = 0
        countableFilters.forEach(countableFilter => {
          if (filter[countableFilter]) result++
        })
        return result + (isPriceFilterVisible ? 0 : filter.price !== '0,100' ? 1 : 0)
      },
      PropTypes.number
    ],

    isAgeFilterActive: [() => [selectors.filter], ({ ages, levels }) => !!ages || !!levels, PropTypes.bool],

    isPriceFilterActive: [() => [selectors.filter], ({ price }) => price !== '0,100', PropTypes.bool],
    isLanguageFilterActive: [
      () => [selectors.filter],
      ({ teacher_languages }) => teacher_languages !== '',
      PropTypes.bool
    ],
    showTeacherSpeaksFilter: [
      () => [selectors.filter],
      filter => {
        const { location_types: locationTypes } = filter
        return locationTypes === 'webcam'
      },
      PropTypes.bool
    ],
    locationFilterContent: [
      () => [selectors.filter, selectors.isLocationFilterActive],
      (filter, isLocationFilterActive) => {
        const { location, location_types: locationTypes } = filter

        if (!isLocationFilterActive && locationTypes !== 'webcam') {
          return I18n.t('bookings.show.lesson_location_extended')
        } else if (locationTypes === 'webcam') {
          return I18n.t('courses.form.webcam')
        } else if (isLocationFilterActive && locationTypes === 'home,studio') {
          return location.split(',')[0]
        } else if (isLocationFilterActive && locationTypes !== 'webcam') {
          const translations = {
            studio: I18n.t('react.search.filters.location.studio'),
            home: I18n.t('react.search.filters.location.home')
          }

          let string = null

          if (locationTypes) {
            string = translations[locationTypes]
          }
          return `${location.split(',')[0]}${string ? ` - ${string}` : ''}`
        }
      },
      PropTypes.string
    ],

    ageFilterContent: [
      () => [selectors.filter],
      ({ levels, ages }) => {
        const agesParts = ages.split(',').filter(aP => !!aP)

        const ageTranslations = {
          infant: I18n.t('react.search.floating_filter.ages.infant'),
          preschool: I18n.t('react.search.floating_filter.ages.preschool'),
          child: I18n.t('react.search.floating_filter.ages.child'),
          teenager: I18n.t('react.search.floating_filter.ages.teenager'),
          adult: I18n.t('react.search.floating_filter.ages.adult'),
          senior: I18n.t('react.search.floating_filter.ages.senior')
        }

        const levelTranslations = {
          beginner: I18n.t('main.filter.level_2'),
          intermediate: I18n.t('main.filter.level_3'),
          advanced: I18n.t('main.filter.level_4')
        }

        const uniqAges = {}
        agesParts.forEach(l => (uniqAges[ageTranslations[l]] = true))
        const sortedAgeParts = Object.keys(uniqAges).sort((a, b) => naturalCompare.caseInsensitive(a, b))

        const ageString =
          agesParts.length > 2
            ? `${I18n.t('activerecord.attributes.message.student_age')} · ${agesParts.length}`
            : sortedAgeParts.join(', ')
        const levelString = levels && levels.length > 0 ? levelTranslations[levels] || levels : null

        if (ageString && levelString) {
          return `${ageString} - ${levelString}`
        }

        if (levelString) {
          return levelString
        }

        if (ageString) {
          return ageString
        }

        return I18n.t('activerecord.attributes.message.student_age')
      },
      PropTypes.string
    ],

    priceFilterContent: [
      () => [selectors.filter, selectors.isPriceFilterActive],
      (filter, isPriceFilterActive) => {
        if (!isPriceFilterActive) {
          return I18n.t('react.search.filters.price_filters_button')
        }
        const priceParts = filter.price.split(',')
        if (priceParts[1] === '100') {
          return formatPrice(parseInt(priceParts[0]), filter.currency)
            .split(`${parseInt(priceParts[0])}`)
            .join(`${parseInt(priceParts[0])}+`)
        }
        return priceParts.map(p => formatPrice(parseInt(p), filter.currency)).join('-')
      },
      PropTypes.string
    ],

    isMoreFiltersActive: [
      () => [selectors.activeFiltersCount],
      activeFiltersCount => activeFiltersCount !== 0,
      PropTypes.bool
    ],

    isCategoryTagsFiltersActive: [
      () => [selectors.filter],
      ({ category_tags: categoryTags }) => {
        return !!categoryTags
      },
      PropTypes.bool
    ],

    shouldIgnoreCategoryTagsFilter: [
      () => [selectors.unsavedFilter, selectors.results],
      (unsavedFilter, results) => {
        if (!unsavedFilter.category_tags || !results.category_tags) {
          return false
        }
        const selectedTagIds = unsavedFilter.category_tags
          .split(',')
          .filter(id => id.trim())
          .map(id => parseInt(id))

        const categoryTags = results.category_tags
        const allTags = {}
        categoryTags.forEach(tag => {
          allTags[tag.id] = tag
        })

        const isApplyDisabled = selectedTagIds.some(tagId => {
          const tag = allTags[tagId]

          if (!tag || !tag.is_master) {
            return false
          }

          const hasSelectedSubTags = categoryTags
            .filter(categoryTag => categoryTag.master_id === tagId)
            .some(subTag => selectedTagIds.includes(subTag.id))

          return !hasSelectedSubTags
        })

        return isApplyDisabled
      },
      PropTypes.bool
    ]
  }),

  takeEvery: ({ actions }) => ({
    [actions.openFilter]: function * (action) {
      const { setOpenFilters } = this.actions
      const { filterType } = action.payload
      const isFullScreenMap = yield this.get('isFullScreenMap')
      const { filters } = yield this.get('filter')

      yield put(setOpenFilters(getNewFilters(isFullScreenMap, filterType, filters)))
    },

    [actions.applyFilter]: function * (action) {
      const { updateFilter, setOpenFilters, clearChanges, clearFilters } = this.actions
      const { nextFilterType } = action.payload
      const unsavedFilter = yield this.get('unsavedFilter')
      const filterChanges = yield this.get('filterChanges')
      const isFullScreenMap = yield this.get('isFullScreenMap')
      const filter = yield this.get('filter')
      const { filters } = filter
      const newNextFilterType = getNewFilters(isFullScreenMap, nextFilterType, filters)

      const changes = Object.keys(filterChanges)

      if (changes && changes.length > 0) {
        // apply filter
        yield put(clearFilters()) // close popup

        let filterToApply = { ...unsavedFilter }

        if ('location' in filterChanges && (!filterChanges.location || filterChanges.location.trim() === '')) {
          if (filter.location) {
            filterToApply.location = filter.location
          }
        }

        if (!('location_slug' in filterChanges) && filter.location_slug) {
          filterToApply.location_slug = filter.location_slug
        }

        yield put(
          updateFilter({
            ...filterToApply,
            page: 1,
            filters: newNextFilterType,
            fromLocationAutocomplete: changes.indexOf('location') > -1
          })
        )
        yield put(clearChanges())
      } else {
        yield put(setOpenFilters(newNextFilterType))
      }
    },

    [actions.clearFilter]: function * (action) {
      const { updateUnsavedFilter, clearSuggestionResults } = this.actions
      const { filterType } = action.payload

      const isSmallScreen = yield this.get('isSmallScreen')
      const filter = yield this.get('filter')

      yield put(updateUnsavedFilter(clearFilterState({ filterType, isSmallScreen, filter })))
      yield put(clearSuggestionResults())
    }
  })
})
